import { useEffect, useRef, useState } from "react";
import { AudioLines, Loader2, Pause, Play, Volume2, VolumeX } from "lucide-react";

import { MediaUnavailable } from "@/components/ui/media-unavailable";

import { Button } from "@/components/ui/button";

export function MessageAudioPlayer({ src }: { src: string }) {
	const player = useRef<HTMLAudioElement>(null);
	const [playing, setPlaying] = useState(false);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);
	const [position, setPosition] = useState(0);
	const [duration, setDuration] = useState(0);
	const [rate, setRate] = useState(1);
	const [muted, setMuted] = useState(false);

	useEffect(() => {
		const element = player.current;

		return () => element?.pause();
	}, []);

	if (failed) {
		return <MediaUnavailable kind="audio" />;
	}

	const elapsed = `${Math.floor(position / 60)}:${String(Math.floor(position % 60)).padStart(2, "0")}`;
	const total =
		duration > 0
			? `${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, "0")}`
			: "--:--";

	return (
		<div className="flex items-center gap-3 py-1">
			<audio
				ref={player}
				src={src}
				preload="metadata"
				onDurationChange={(event) => {
					if (Number.isFinite(event.currentTarget.duration)) {
						setDuration(event.currentTarget.duration);
					}
				}}
				onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
				onPlay={() => setPlaying(true)}
				onPlaying={() => setLoading(false)}
				onWaiting={() => setLoading(true)}
				onPause={() => {
					setPlaying(false);
					setLoading(false);
				}}
				onEnded={() => {
					setPlaying(false);
					setLoading(false);
				}}
				onError={() => {
					setFailed(true);
					setPlaying(false);
					setLoading(false);
				}}
			/>
			<Button
				size="icon"
				className="size-12 shrink-0 rounded-full shadow-none md:size-12 [&_svg]:size-5"
				aria-label={playing ? "Pausar áudio" : "Ouvir áudio"}
				aria-busy={loading}
				onClick={() => {
					if (!player.current) {
						return;
					}

					if (!player.current.paused) {
						player.current.pause();
						return;
					}

					setLoading(true);
					player.current.play().catch(() => {
						setPlaying(false);
						setLoading(false);
					});
				}}
			>
				{loading && <Loader2 className="motion-safe:animate-spin" />}
				{!loading && playing && <Pause className="fill-current" />}
				{!loading && !playing && <Play className="ml-0.5 fill-current" />}
			</Button>
			<div className="min-w-0 flex-1">
				<div className="flex h-7 items-center justify-between gap-2">
					<span className="flex items-center gap-1.5 text-xs font-medium text-primary">
						<AudioLines className="size-3.5" aria-hidden="true" />
						Áudio
					</span>
					<Button
						variant="ghost"
						className="h-7 min-w-10 rounded-full bg-primary/5 px-2 font-mono text-xs text-primary hover:bg-primary/10 md:h-7"
						aria-label={`Velocidade ${rate}x. Alterar velocidade`}
						onClick={() => {
							const next = rate === 2 ? 1 : rate + 0.5;

							if (player.current) {
								player.current.playbackRate = next;
								setRate(next);
							}
						}}
					>
						{rate}x
					</Button>
				</div>
				<>
					<div className="relative flex h-6 items-center">
						<div
							aria-hidden="true"
							className="h-1.5 w-full overflow-hidden rounded-full bg-primary/15"
						>
							<div
								className="h-full rounded-full bg-primary"
								style={{ width: `${duration > 0 ? (position / duration) * 100 : 0}%` }}
							/>
						</div>
						<input
							type="range"
							min={0}
							max={duration || 1}
							step={0.1}
							value={position}
							disabled={duration <= 0}
							aria-label="Posição do áudio"
							aria-valuetext={`${elapsed} de ${total}`}
							className="message-audio-seek absolute inset-0 h-6 w-full cursor-pointer appearance-none rounded-md bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default"
							onChange={(event) => {
								if (player.current) {
									player.current.currentTime = Number(event.target.value);
									setPosition(Number(event.target.value));
								}
							}}
						/>
					</div>
					<div className="flex h-6 items-center justify-between gap-2">
						<div className="flex items-center gap-2 font-mono text-xs tabular-nums">
							<span className="text-primary">{elapsed}</span>
							<span className="text-muted-foreground">/ {total}</span>
						</div>
						<Button
							variant="ghost"
							size="icon"
							className="size-7 rounded-full text-muted-foreground hover:bg-primary/10 hover:text-primary md:size-7 [&_svg]:size-3.5"
							aria-label={muted ? "Ativar som" : "Silenciar áudio"}
							aria-pressed={muted}
							onClick={() => {
								if (player.current) {
									player.current.muted = !muted;
									setMuted(!muted);
								}
							}}
						>
							{muted && <VolumeX />}
							{!muted && <Volume2 />}
						</Button>
					</div>
				</>
			</div>
		</div>
	);
}
